import React, { useState, useRef, useEffect, useCallback } from 'react';

type NominatimAddress = {
  house_number?: string;
  road?: string;
  pedestrian?: string;
  footway?: string;
  cycleway?: string;
  path?: string;
  postcode?: string;
  city?: string;
  town?: string;
  village?: string;
  municipality?: string;
};

type NominatimResult = {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  address?: NominatimAddress;
};

function formatShortAddress(result: NominatimResult): string {
  const addr = result.address;
  if (!addr) return result.display_name;

  const street = addr.road || addr.pedestrian || addr.footway || addr.cycleway || addr.path || '';
  const number = addr.house_number || '';
  const postcode = addr.postcode || '';
  const city = addr.city || addr.town || addr.village || addr.municipality || '';

  const streetPart = number ? `${number} ${street}` : street;
  const parts = [streetPart, postcode, city].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : result.display_name;
}

type OSMAutocompleteProps = {
  className?: string;
  id?: string;
  defaultValue?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onPlaceSelected?: (place: NominatimResult) => void;
};

export function OSMAutocomplete({
  className,
  id,
  defaultValue,
  onBlur,
  onPlaceSelected,
}: OSMAutocompleteProps): JSX.Element {
  const [value, setValue] = useState(defaultValue || '');
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const searchAddress = useCallback(async (query: string) => {
    if (query.length < 3) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&limit=5`,
      );
      const data: NominatimResult[] = await response.json();
      setSuggestions(data);
      setIsOpen(data.length > 0);
    } catch (error) {
      console.error('Error on address search:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchAddress(newValue);
    }, 300);
  };

  const handleSelectSuggestion = (suggestion: NominatimResult) => {
    setValue(formatShortAddress(suggestion));
    setSuggestions([]);
    setIsOpen(false);
    if (onPlaceSelected) {
      onPlaceSelected(suggestion);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      setIsOpen(false);
    }, 200);
    if (onBlur) {
      onBlur(e);
    }
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="osm-autocomplete" ref={containerRef}>
      <input
        className={className}
        id={id}
        type="text"
        value={value}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        autoComplete="off"
      />
      {isLoading && <div className="osm-autocomplete__loading" />}
      {isOpen && suggestions.length > 0 && (
        <ul className="osm-autocomplete__suggestions">
          {suggestions.map((suggestion) => (
            <li
              key={suggestion.place_id}
              className="osm-autocomplete__suggestion"
              onClick={() => handleSelectSuggestion(suggestion)}
            >
              {formatShortAddress(suggestion)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
